import React, { useMemo, useState } from 'react';
import { Group } from '../../../../../redux/groups/api/group.model';
import ConnectedGroupUserAssign from './GroupUserAssign';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { useAppSelector } from '../../../../../redux/store.model';
import { getPolyglot } from '../../../../../i18n';
import {
  Stack,
  Tooltip,
  Typography,
  ButtonGroup,
  IconButton,
  Box,
  Avatar,
} from '@mui/material';

import { getFeatureToggle } from '../../../../../featureToggle';
import { getMyWorkspaceSelector } from '../../../../../redux/stoerkID/selectors/StoerkId.selectors';
import SortUtil from '../../../../../util/SortUtil';
import { getRightUserUtilSelector } from '../../../../../redux/rights/selectors';
import GroupUserUpdateRole from './GroupUserUpdateRole/GroupUserUpdateRole';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { RightUserAPIResponse } from '../../../../../redux/rights/api/right.model';
import { getUserRolesNamesDescriptions } from './Constants';
import StoerkIdNoStoerkIdModal from '../../../../StoerkId/MyStoerkId/StoerkIdNoStoerkIdModal';
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  UserIcon,
} from '../../../../../theme/icons';
import useUsers from '../../../../../redux/users/hooks/useUsers';
import DeleteUserConfirmationDialog from './DeleteUserConfirmationDialog/DeleteUserConfirmationDialog';
import { getMyUserId } from '../../../../../redux/auth/selectors';
import useUserRolesGroup from '../../../../../redux/rights/hooks/useUserRolesGroup';

interface GroupEditUsersProps {
  group?: Group;
}

interface UserObj {
  roles?: RightUserAPIResponse[];
  id: string;
  encodedId: string;
  name: string | null;
  location: string | null;
  email: string;
  avatarLocation: string | null;
}
function GroupEditUsers({ group }: GroupEditUsersProps) {
  const showError = useShowErrorMessage();
  const polyglot = getPolyglot();
  const featuresToggles = useAppSelector(getFeatureToggle);
  const loggedUserId = useAppSelector(getMyUserId);

  const usersIds = group?.users;
  const { data: users = [], ...rest } = useUsers(usersIds);
  // const users = [] as IUser[];
  const workspace = useAppSelector(getMyWorkspaceSelector);
  const rightsUserUtil = useAppSelector(getRightUserUtilSelector);
  const { data: usersRolesGroup, isLoading: showMessageLoadData } =
    useUserRolesGroup(group?.id);
  const [showMessageDeleteGroupUser, setShowMessageDeleteGroupUser] =
    useState<boolean>(false);
  const [showGroupUserAssign, setShowGroupUserAssign] =
    useState<boolean>(false);
  const [showGroupUserUpdateRole, setShowGroupUserUpdateRole] =
    useState<boolean>(false);
  const [showNoWorkspace, setShowNoWorkspace] = useState<boolean>(false);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<
    Partial<UserObj & { rolesId: string[] }>
  >({
    rolesId: [],
    id: '',
    name: '',
  });

  const loadComponentDisplayProperties = () => {
    const showRemoveGroupUserBotton =
      group &&
      rightsUserUtil.hasRightsToRemoveGroupUser(group.id, workspace?.id ?? '');
    const showAssignGroupUserBotton =
      group &&
      rightsUserUtil.hasRightsToAssignGroupUser(group.id, workspace?.id ?? '');
    const showUserRights =
      group &&
      rightsUserUtil.hasRightsToReadUserRights(group.id, workspace?.id ?? '');
    const showUpdateRoleGroupUser =
      group &&
      rightsUserUtil.hasRightsToAssignGroupUser(group.id, workspace?.id ?? '');
    return {
      showRemoveGroupUserBotton,
      showAssignGroupUserBotton,
      showUserRights,
      showUpdateRoleGroupUser,
    };
  };

  const {
    showRemoveGroupUserBotton,
    showAssignGroupUserBotton,
    showUserRights,
    showUpdateRoleGroupUser,
  } = loadComponentDisplayProperties();

  const openGroupUserAssign = async () => {
    try {
      if (
        !workspace &&
        featuresToggles?.stoerkId &&
        featuresToggles?.stoerkIdRestrictions
      ) {
        setShowNoWorkspace(true);
        return;
      }
      // Was decide because security don't show users on workspace
      // if (group) await dispatch(fetchAvailableUsersToAssignGroup(group.id));
      // if (group) await dispatch(fetchAvailableUsersToAssignGroup(group.id));
      setShowGroupUserAssign(true);
    } catch (error) {
      setShowGroupUserAssign(true);
      showError(error);
    }
  };

  const usersWhitRoles = users.map((user) => {
    const userRoleGroup = usersRolesGroup?.[user.id as string];
    return {
      ...user,
      roles: userRoleGroup,
    };
  });

  const sortField = 'name';
  const resultUsers = SortUtil.multisort(
    usersWhitRoles,
    [sortField],
    undefined
  );

  /**
   * Close group user assign
   * @param bool addedUser
   */
  const closeGroupUserAssign = async (addedUser = false) => {
    setShowGroupUserAssign(false);
  };

  /**
   * Close group user update role
   */
  const closeGroupUserUpdateRole = async (updatedUser = false) => {
    setShowGroupUserUpdateRole(false);
  };

  /**
   * Open message delete group user
   * this function opens a modal window with a message question
   */
  const openMessageDeleteGroupUser = (event: unknown, userId: string) => {
    setShowMessageDeleteGroupUser(true);
    setDeleteUserId(userId);
  };

  /**
   * Close message delete group user
   */
  const closeMessageDeleteGroupUser = () => {
    setShowMessageDeleteGroupUser(false);
    setDeleteUserId(null);
  };

  /**
   * Open group user update role
   * @param object event
   * @param string userId
   * @param string userName
   * @param array rolesId
   */
  const openGroupUserUpdateRole = (
    event: unknown,
    userId: string,
    userName: string,
    rolesId: string[]
  ) => {
    setSelectedUser({ id: userId, name: userName, rolesId: rolesId });
    setShowGroupUserUpdateRole(true);
  };

  const buttonAdd = (
    <Tooltip title={polyglot.t('group.tooltip.open_window_assign_user')}>
      <IconButton
        color="primary"
        size="large"
        sx={{ float: 'right' }}
        onClick={openGroupUserAssign}
      >
        <AddIcon />
      </IconButton>
    </Tooltip>
  );

  const buttonDelete = (index: number, userId: string) => {
    return (
      <Tooltip title={polyglot.t('group.tooltip.remove_group_user')}>
        <IconButton
          id={`iconDeleteUser${index}`}
          onClick={(event) => openMessageDeleteGroupUser(event, userId)}
          size="small"
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const buttonEdit = (user: UserObj) => {
    return (
      <Tooltip title={polyglot.t('group.tooltip.update_role_user')}>
        <IconButton
          id={`iconEditUser${user.id}`}
          onClick={(event) => {
            return openGroupUserUpdateRole(
              event,
              user.id,
              user.name || user.email,
              user.roles?.map((r) => r.id) ?? []
            );
          }}
          size="small"
        >
          <EditIcon />
        </IconButton>
      </Tooltip>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'avatarLocation',
      headerName: '',
      sortable: false,
      minWidth: 60,
      flex: 0.5,
      renderCell: (params) => (
        <Avatar src={params.value} alt="user-avatar" sx={{ m: 0.3 }}>
          {params.value ? <UserIcon /> : null}
        </Avatar>
      ),
    },
    {
      field: 'name',
      headerName: polyglot.t('group.users.name'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2">
          {showUserRights ? params.value : '—'}
        </Typography>
      ),
    },
    {
      field: 'role',
      headerName: polyglot.t('group.users.role'),
      sortable: false,
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        const row = params.row as UserObj;
        const userRoleGroup = usersRolesGroup?.[row.id as string];
        const validRoles =
          userRoleGroup?.filter((role) => role.role !== undefined) ?? [];
        const userRoles = validRoles.length
          ? getUserRolesNamesDescriptions(validRoles).map((r, id) => {
              return (
                <div key={id.toString()}>
                  {
                    <Box display="flex" alignItems="center">
                      {`${r.name} `}
                      {r.description}
                    </Box>
                  }
                </div>
              );
            })
          : params.value
              ?.split(',')
              .sort()
              .map((name: string, id: number) => (
                <div key={id.toString()}>{name}</div>
              ));
        return (
          <Typography variant="body2" component={'div'}>
            {showUserRights ? userRoles : '—'}
          </Typography>
        );
      },
    },
    {
      field: 'email',
      headerName: polyglot.t('group.users.email'),
      sortable: false,
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      flex: 0.5,
      minWidth: 50,
      renderCell: (params) => {
        const row = params.row as UserObj;
        return (
          <ButtonGroup>
            {showUpdateRoleGroupUser && buttonEdit(row)}
            {loggedUserId !== row.id &&
              showRemoveGroupUserBotton &&
              buttonDelete(+row.id, row.id)}
          </ButtonGroup>
        );
      },
    },
  ];

  const rows = useMemo(
    () =>
      resultUsers.map((user, index) => {
        return {
          ...user,
          name: user.name || polyglot.t('group.users.no_name'),
          actions: user,
        } as UserObj;
      }),
    [resultUsers]
  );

  return (
    <>
      {showGroupUserAssign && group && (
        <ConnectedGroupUserAssign
          groupId={group?.id}
          closeGroupUserAssign={closeGroupUserAssign}
        />
      )}
      {showGroupUserUpdateRole && (
        <GroupUserUpdateRole
          groupId={group?.id ?? ''}
          userId={selectedUser.id ?? ''}
          userName={selectedUser.name ?? ''}
          rolesId={selectedUser.rolesId ?? []}
          showGroupUserUpdateRole={showGroupUserUpdateRole}
          closeGroupUserUpdateRole={closeGroupUserUpdateRole}
        />
      )}

      <Stack spacing={1} width={'100%'}>
        <Box
          sx={{
            height: '350px',
            width: '100%',
            overflow: 'auto',
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={showMessageLoadData}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
          />
        </Box>

        <Box>{showAssignGroupUserBotton && buttonAdd}</Box>
      </Stack>

      <StoerkIdNoStoerkIdModal
        workspace={workspace}
        open={!!showNoWorkspace}
        onClose={() => setShowNoWorkspace(false)}
      />
      <DeleteUserConfirmationDialog
        open={showMessageDeleteGroupUser}
        onClose={closeMessageDeleteGroupUser}
        userId={deleteUserId}
        groupId={group?.id}
      />
    </>
  );
}

export default React.memo(GroupEditUsers);
